import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { Database } from "@/integrations/supabase/types";
import type { PaginationState } from "./usePagination";

type InvoiceStatus = Database["public"]["Enums"]["invoice_status"];

export type InvoiceFilterStatus = InvoiceStatus | "all";

export const useInvoices = (statusFilter: InvoiceFilterStatus = "draft", pagination?: PaginationState) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["invoices", statusFilter, user?.id, pagination?.page],
    queryFn: async () => {
      if (pagination) {
        let countQuery = supabase
          .from("invoices")
          .select("id", { count: "exact", head: true })
          .is("deleted_at", null);
        if (statusFilter !== "all") countQuery = countQuery.eq("status", statusFilter);
        const { count } = await countQuery;
        pagination.setTotalCount(count ?? 0);
      }

      let query = supabase
        .from("invoices")
        .select("id, invoice_number, invoice_date, total, status, approved_by, approved_at, sent_at, paid_at, payment_reference, due_date, amount_paid, created_at, project_id, projects(name)")
        .is("deleted_at", null)
        .order("created_at", { ascending: false });

      if (statusFilter !== "all") query = query.eq("status", statusFilter);

      if (pagination) {
        query = query.range(pagination.from, pagination.to);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!user,
  });
};
